// Centralized portfolio data.
// All text content is preserved verbatim from the original site.

export const experiences = [
    {
        id: 'exp-diyrocks-de',
        type: 'experience',
        role: 'Senior Frontend Engineer',
        company: 'DIY ROCKS (HK) Limited',
        date: 'Jan 2026 - Aug 2026',
        sortDate: '2026-01',
        bullets: [
            'Act as technical liaison between product, external dev teams, and internal stakeholders, translating business requirements into actionable specifications.',
            'Manage white-label order systems, verifying configurator apps transmit accurate data to factory production pipelines.',
            'Coordinate across branches including China production team to ensure SKUs, pricing, and production specs are correctly structured and delivered.',
            'Expand configurator application functionality with new products, features, and system reliability improvements.',
            'Ensure end-to-end data integrity between 3D configurator, B2B order flow, and downstream production systems.',
        ],
    },
    {
        id: 'exp-diyrocks-fd',
        type: 'experience',
        role: 'Frontend Developer',
        company: 'DIY ROCKS (HK) Limited',
        date: 'Jan 2025 - Dec 2025',
        sortDate: '2025-01',
        bullets: [
            'Led migration of legacy Vue 2 projects to Vue 3, modernizing the codebase while preserving full core functionality.',
            'Built interactive, real-time 3D jewellery customization tools that served as the core selling point of the B2B platform.',
            'Drove a full product redesign to unify visual identity across all applications, strengthening brand recognition.',
            'Architected a container application hosting multiple configurator apps (Bridal, Jewellery, Wedding), defining inter-app communication protocols and data contracts.',
            'Designed and implemented a white-label multi-tenant system enabling per-customer customization of design themes, feature toggles, and branding deployed as an embeddable application.',
            'Collaborated closely with designers to validate design feasibility against technical constraints, identifying trade-offs and aligning on implementation scope.',
            'Provided direct customer-facing technical support, resolving software issues and answering product questions.',
        ],
    },
    {
        id: 'exp-fletrix',
        type: 'experience',
        role: 'Software Engineer',
        company: 'Fletrix Limited',
        date: 'Oct 2022 - Jun 2024',
        sortDate: '2022-10',
        bullets: [
            'Built and maintained ERP scheduling module, CRUD interfaces for customer/booking/order data, and role-based access control using CASL on both frontend and backend.',
            'Developed full-stack features in an agile environment using VuetifyJS and NestJS across an internal ERP booking system and a companion SaaS POS-linked application.',
            'Built SaaS POS integration layer, syncing products, staff, and order data bidirectionally between POS devices and the ERP system.',
            'Developed a live analytics dashboard powered by complex MongoDB aggregation pipelines with selectable filters for real-time business insights.',
            'Designed UX-friendly interfaces with optimistic UI patterns in the SaaS product, ensuring responsive and seamless user experience.',
            'Built reusable frontend components to improve development efficiency and visual consistency across both platforms.',
        ],
    },
    {
        id: 'exp-wealthskey-pt',
        type: 'experience',
        role: 'Software Developer (Part-Time)',
        company: 'Wealthskey Limited',
        date: 'Feb 2021 - Oct 2021',
        sortDate: '2021-02',
        bullets: [
            'Built an international retail property website from scratch using ReactJS and NextJS.',
            'Implemented a subscription system with flexible pricing plans and PayPal REST API payment integration.',
            'Integrated Zoom OAuth API to allow users to schedule meetings directly from the platform.',
            'Saved API call responses to MongoDB and Elasticsearch database.',
            'Built frontend features including search with suggestions, property publishing flow, and page view tracking.',
        ],
    },
    {
        id: 'exp-wealthskey-intern',
        type: 'experience',
        role: 'Software Developer (Intern)',
        company: 'Wealthskey Limited',
        date: 'Dec 2020 - Feb 2021',
        sortDate: '2020-12',
        bullets: [
            'Created a Facebook chatbot using Chatfuel with conversational flowchart logic.',
            'Developed a user-friendly reservation page, strengthening frontend development skills.',
        ],
    },
];

export const educations = [
    {
        id: 'edu-hkust',
        type: 'education',
        degree: 'Bachelor of Engineering in Computer Engineering',
        institution: 'HKUST',
        institutionFull: 'The Hong Kong University of Science and Technology',
        date: 'Sept 2019 - Aug 2022',
        sortDate: '2019-09',
        logo: 'https://i.imgur.com/gAcsR1B.png',
        courses: [
            'Internet Computing',
            'Mobile Application Development',
            'Discrete Mathematical Tools for Computer Science',
            'Industrial Experience',
        ],
    },
    {
        id: 'edu-cccu',
        type: 'education',
        degree: 'Associate of Engineering',
        institution: 'CCCU',
        institutionFull: 'Community College of City University',
        date: 'Sept 2017 - Aug 2019',
        sortDate: '2017-09',
        logo: 'https://i.imgur.com/mignBUa.png',
        courses: [
            'Introduction to Programming',
            'Object-Oriented Programming and Design',
            'Data Structure and Algorithms',
        ],
    },
];

export const projects = [
    {
        id: 'proj-ticket-challenger',
        type: 'project',
        title: 'Ticket Challenger',
        date: '',
        sortDate: '2024-01',
        tech: 'HTML5, CSS3, JavaScript',
        link: 'https://github.com/jlimbu1/ticket-challenger',
        linkLabel: 'github.com/jlimbu1/ticket-challenger',
        description:
            'An online ticket purchasing experience simulator to practice ticket-nabbing skills.',
    },
    {
        id: 'proj-arm-mooc',
        type: 'project',
        title: 'MOOC Web Platform for ARM Assembly Language',
        date: '',
        sortDate: '2022-06',
        tech: 'MongoDB, ExpressJS, ReactJS, NodeJS, Docker, Kubernetes',
        link: 'https://drive.google.com/file/d/16zJW9AI0NrY946dJSZ8ZFHIefUuvtC9p/view?usp=sharing',
        linkLabel: 'Video Demo',
        description:
            'A web application for university courses teaching ARM assembly language programming. No installation or hardware required. Built with the MERN stack and deployed via Docker on DigitalOcean Kubernetes.',
    },
    {
        id: 'proj-danger-dungeon',
        type: 'project',
        title: 'Danger Dungeon (Browser Game)',
        date: '',
        sortDate: '2022-01',
        tech: 'HTML5, CSS3, JavaScript, WebSocket',
        link: 'https://drive.google.com/file/d/1VyiSmQ8MtSxPNo72K501zlJUbuuvc9ei/view?usp=sharing',
        linkLabel: 'Video Demo',
        description:
            'A JavaScript-intensive 2-player browser game featuring sprite animations with internal frame timers, real-time player movement sync, and monster spawning via WebSockets.',
    },
    {
        id: 'proj-arduino-gameboy',
        type: 'project',
        title: 'Arduino Game Boy',
        date: '',
        sortDate: '2019-06',
        tech: 'C/C++, Arduino, OOP',
        link: 'https://drive.google.com/file/d/12XV0AEYPPKn6YgKdmOQXr42JPOreNfrJ/view?usp=sharing',
        linkLabel: 'Video Demo',
        description:
            'A Game Boy device built with Arduino as the microcontroller. Written in C++ using OOP methodology with LCD screen library for drawing platforms and entities.',
    },
    {
        id: 'proj-game-guide',
        type: 'project',
        title: 'Game Guide Page',
        date: '',
        sortDate: '2021-06',
        tech: 'HTML5, CSS3, JavaScript',
        link: 'https://chillis.netlify.app',
        linkLabel: 'chillis.netlify.app',
        description:
            'An information hub for an MMORPG guild, designed to be simple enough for other members to contribute to over time.',
    },
];

// Skills grouped by usage context.
// `targets` lists the entry ids (experience/project/education) where the skill applies.
export const skillGroups = [
    {
        key: 'work',
        label: 'Work Experience',
        dotClass: 'dotWork',
        skills: [
            {
                key: 'vue',
                label: 'Vue 2/3',
                icon: 'devicon-vuejs-plain',
                years: '3 yrs',
                targets: ['exp-diyrocks-fd', 'exp-fletrix'],
            },
            {
                key: 'javascript',
                label: 'JavaScript',
                icon: 'devicon-javascript-plain',
                years: '3 yrs',
                targets: [
                    'exp-diyrocks-fd',
                    'exp-fletrix',
                    'exp-wealthskey-pt',
                    'proj-danger-dungeon',
                    'proj-ticket-challenger',
                    'proj-game-guide',
                ],
            },
            {
                key: 'typescript',
                label: 'TypeScript',
                icon: 'devicon-typescript-plain',
                years: '1 yr',
                targets: ['exp-diyrocks-fd', 'exp-fletrix'],
            },
            {
                key: 'nodejs',
                label: 'NodeJS',
                icon: 'devicon-nodejs-plain',
                years: '2 yrs',
                targets: ['exp-fletrix', 'proj-arm-mooc'],
            },
            {
                key: 'react',
                label: 'React',
                icon: 'devicon-react-original',
                years: '<1 yr',
                targets: ['exp-wealthskey-pt'],
            },
            {
                key: 'nextjs',
                label: 'NextJS',
                icon: 'devicon-nextjs-plain',
                targets: ['exp-wealthskey-pt'],
            },
            {
                key: 'nuxtjs',
                label: 'NuxtJS',
                icon: 'devicon-nuxtjs-plain',
                targets: ['exp-fletrix'],
            },
            {
                key: 'tailwind',
                label: 'Tailwind CSS',
                icon: 'devicon-tailwindcss-plain',
                targets: ['exp-diyrocks-fd'],
            },
            {
                key: 'bootstrap',
                label: 'Bootstrap',
                icon: 'devicon-bootstrap-plain',
                targets: ['exp-wealthskey-pt'],
            },
            {
                key: 'express',
                label: 'ExpressJS',
                icon: 'devicon-express-original',
                years: '2 yrs',
                targets: ['proj-arm-mooc', 'exp-wealthskey-pt'],
            },
            {
                key: 'nest',
                label: 'NestJS',
                icon: 'devicon-nestjs-plain',
                years: '1 yr',
                targets: ['exp-fletrix'],
            },
            {
                key: 'mongodb',
                label: 'MongoDB',
                icon: 'devicon-mongodb-plain',
                years: '2 yrs',
                targets: ['exp-fletrix', 'exp-wealthskey-pt', 'proj-arm-mooc'],
            },
            {
                key: 'socketio',
                label: 'Socket.IO',
                icon: 'devicon-socketio-original',
                targets: ['proj-danger-dungeon'],
            },
            { key: 'jwt', label: 'JWT', targets: ['exp-fletrix'] },
            {
                key: 'microservices',
                label: 'Microservices',
                targets: ['exp-fletrix', 'proj-arm-mooc'],
            },
        ],
    },
    {
        key: 'tools',
        label: 'Tools & Infrastructure',
        dotClass: 'dotTools',
        skills: [
            {
                key: 'git',
                label: 'Git',
                icon: 'devicon-git-plain',
                years: '3 yrs',
                targets: [
                    'proj-ticket-challenger',
                    'proj-arm-mooc',
                    'proj-danger-dungeon',
                    'proj-arduino-gameboy',
                    'proj-game-guide',
                ],
            },
            {
                key: 'aws',
                label: 'AWS EC2',
                icon: 'devicon-amazonwebservices-plain',
                targets: ['proj-arm-mooc'],
            },
            {
                key: 'docker',
                label: 'Docker',
                icon: 'devicon-docker-plain',
                targets: ['proj-arm-mooc'],
            },
            {
                key: 'kubernetes',
                label: 'Kubernetes',
                icon: 'devicon-kubernetes-plain',
                targets: ['proj-arm-mooc'],
            },
            {
                key: 'linux',
                label: 'Linux',
                icon: 'devicon-linux-plain',
                targets: ['proj-arm-mooc'],
            },
            { key: 'xampp', label: 'XAMPP', targets: ['proj-game-guide'] },
        ],
    },
    {
        key: 'personal',
        label: 'Personal & Education Projects',
        dotClass: 'dotPersonal',
        skills: [
            {
                key: 'cpp',
                label: 'C/C++',
                icon: 'devicon-cplusplus-plain',
                years: '2 yrs',
                targets: ['proj-arduino-gameboy', 'edu-cccu'],
            },
            {
                key: 'java',
                label: 'Java',
                icon: 'devicon-java-plain',
                years: '1 yr',
                targets: ['edu-cccu'],
            },
            {
                key: 'arduino',
                label: 'Arduino',
                icon: 'devicon-arduino-plain',
                targets: ['proj-arduino-gameboy'],
            },
            {
                key: 'html5',
                label: 'HTML5',
                icon: 'devicon-html5-plain',
                targets: ['proj-ticket-challenger', 'proj-danger-dungeon', 'proj-game-guide'],
            },
            {
                key: 'css3',
                label: 'CSS3',
                icon: 'devicon-css3-plain',
                targets: ['proj-ticket-challenger', 'proj-danger-dungeon', 'proj-game-guide'],
            },
        ],
    },
    {
        key: 'lang',
        label: 'Spoken Languages',
        dotClass: 'dotLang',
        skills: [
            { key: 'english', label: 'English (Fluent)' },
            { key: 'nepali', label: 'Nepali (Native)' },
            { key: 'cantonese', label: 'Cantonese (Conversational)' },
        ],
    },
];
